import type {
    CreativeXCampaignObjectiveResponse,
    CreativeXChannel,
    CreativeXPlacements,
    CreativeXPublishers
} from "@/types/preflight/creativeX";
import { resourceListRequest } from "@/utils/CreativeIntelligenceApi/resourceManager";
import { PreflightStatus } from "hoxton-shared/enums/creativex";
import { PLATFORM } from "hoxton-shared/enums/exports";

type CreativeXRawGuideline = {
    name: string;
    weight: string;
    passed: boolean;
};

type CreativeXRawScore = {
    default: boolean;
    cqs: boolean;
    name: string;
    guidelines: CreativeXRawGuideline[];
    creativeQualityTier: string;
    value: number;
    status: string;
};

type CreativeXRawCreative = {
    postId: number;
    sourceUrl: string;
    externalLabel?: string;
    scorecardUrl?: string;
    scores: CreativeXRawScore[];
};

type CreativeXRawOutput = {
    market: {
        name: string;
        iso_3166Code: string;
    } | null;
    createdAt: string;
    completedAt: string;
    requestId: number;
    name: string;
    channel: CreativeXChannel;
    creatives: CreativeXRawCreative[];
    publisher: CreativeXPublishers | null;
    placement: CreativeXPlacements | null;
    brand: {
        name: string;
        id: number;
    } | null;
    contentType: "brand" | "creator" | null;
    status: "processing" | "completed" | "preflight_deleted" | "received" | "error";
    errors?: { code: number; message: string }[] | null[] | null;
    adFormat?: string | null;
    campaignObjective?: CreativeXCampaignObjectiveResponse | null;
};

type CreativeXResourceInput = {
    campaignObjective: CreativeXCampaignObjectiveResponse;
    brandId: number;
    channel: CreativeXChannel;
    name: string;
    creatives: { fileLocation: { bucket: string; key: string; signedUrl: string } }[];
    marketCode: string;
    publisher: CreativeXPublishers;
    placement: CreativeXPlacements;
    contentType: "brand" | "creator";
};

type NormalizedCreativexReport = {
    completedAt: string;
    name: string;
    creative: {
        scorecardUrl?: string;
        score: CreativeXRawScore | null;
    };
    errors?: { code: number; message: string }[] | null[] | null;
    requestId: number;
    publisher: CreativeXPublishers | null;
    placement: CreativeXPlacements | null;
    channel: CreativeXChannel | null;
};

export type CreativeXPreflightHistory =
    | { previewUrl: string | undefined; created: string; status: "ERROR"; error: string }
    | { previewUrl: string | undefined; created: string; status: "PROCESSING" }
    | {
          previewUrl: string | undefined;
          created: string;
          status: "COMPLETED";
          report: NormalizedCreativexReport;
      };

export async function getPreflightHistory(
    deliverableId: string,
    platform: typeof PLATFORM.CREATIVEX
): Promise<CreativeXPreflightHistory[]> {
    if (platform !== PLATFORM.CREATIVEX) {
        return [];
    }

    return getCreativeXHistory(deliverableId);
}

async function getCreativeXHistory(deliverableId: string): Promise<CreativeXPreflightHistory[]> {
    const { data } = await resourceListRequest<CreativeXRawOutput, CreativeXResourceInput>({
        id: deliverableId
    });

    const rawHistory = sortHistoryByCreatedDate(data.items);

    const outputs = rawHistory.reduce<CreativeXPreflightHistory[]>((acc, { input, output, created }) => {
        const previewUrl = input.creatives[0]?.fileLocation.signedUrl;

        const historyItem = {
            created,
            previewUrl
        } as {
            created: string;
            previewUrl: string | undefined;
            status?: "ERROR" | "PROCESSING" | "COMPLETED";
            error?: string;
            report?: NormalizedCreativexReport;
            requestId?: number;
            publisher?: CreativeXPublishers | null;
            placement?: CreativeXPlacements | null;
            channel?: CreativeXChannel | null;
        };

        if (output?.fail) {
            historyItem.status = PreflightStatus.ERROR;
            historyItem.error = output.fail as string;
        } else if (output?.success) {
            historyItem.status = PreflightStatus.COMPLETED;
            historyItem.report = {
                completedAt: output.success.completedAt,
                name: output.success.name,
                creative: {
                    scorecardUrl: output.success.creatives[0]?.scorecardUrl,
                    score: output.success.creatives[0]?.scores.find(score => score.default) ?? null
                },
                errors: output.success.errors,
                requestId: output.success.requestId,
                publisher: output.success.publisher,
                placement: output.success.placement,
                channel: output.success.channel
            };
        } else if (!output?.fail && !output?.success) {
            historyItem.status = PreflightStatus.PROCESSING;
        } else {
            historyItem.status = PreflightStatus.ERROR;
            historyItem.error = "Unknown error";
        }

        acc.push(historyItem as CreativeXPreflightHistory);

        return acc;
    }, []);

    return outputs;
}

function sortHistoryByCreatedDate<T extends { created?: string }>(items: T[]): T[] {
    return items.sort((a, b) => {
        if (!a.created || !b.created) {
            return 0;
        }

        const aCreatedAt = new Date(a.created).getTime();
        const bCreatedAt = new Date(b.created).getTime();

        if (aCreatedAt < bCreatedAt) {
            return 1;
        }
        if (aCreatedAt > bCreatedAt) {
            return -1;
        }
        return 0;
    });
}
